/** The reachability audit: the last word on whether an object stays.
 *
 * Runs once on the surface from a capital and once underground from the gate
 * mouths, which is why it is a module of its own below both `generate.ts` and
 * `underworld.ts`.
 */

import { UNREACHABLE_COST, computeReachable, moveRules } from "@game/core";
import { terrainOf } from "./grid.js";
import { removePlacement } from "./place.js";
import { renumber } from "./placement.js";
import type { GenState } from "./state.js";

/**
 * The last word, and the only pass that uses the kernel's own pathfinder.
 *
 * `computeReachable` reports true cheapest distance rather than a
 * budget-limited set, so `cost < UNREACHABLE_COST` reads as "a hero on foot can
 * get here eventually". Anything whose entrance fails that is removed: an
 * object a player cannot reach is worse than an empty tile, because it looks
 * usable.
 *
 * `origins` is one cell on the surface (every town is reachable from every
 * other, so the first one answers for all of them) and every gate mouth
 * underground - down there the only way in is a gate, and a chamber that no
 * gate opens onto is exactly the isolated pocket this pass exists to empty.
 * `computeReachable` never leaves the level its origin is on, so this is a
 * per-level question by construction.
 */
export function auditReachability(s: GenState, origins: readonly number[]): void {
  if (origins.length === 0) return;
  const rules = moveRules({ mode: "land", budget: 0x7fffffff, lastStepFree: false });

  for (let round = 0; round < 3; round++) {
    const sets = origins.map((o) => computeReachable(s.map, o, rules, {}));
    const reached = (cell: number): boolean =>
      sets.some((set) => set.cost[cell]! < UNREACHABLE_COST);
    const doomed: number[] = [];
    for (let i = 0; i < s.objects.length; i++) {
      const p = s.placements[i];
      if (p === undefined || p.entrances.length === 0) continue;
      if (p.def.kind === "town") continue;
      if (p.def.id.startsWith("boat_") || p.def.id === "tp_whirlpool") continue;
      if (p.def.id.startsWith("expansion-sea") || p.def.id === "bld_jetsam") continue;
      if (p.def.id === "bank_shipwreck") continue;
      if (p.entrances.every((e) => terrainOf(s.map, e) === "water")) continue;
      if (p.entrances.some(reached)) continue;
      doomed.push(i);
    }
    if (doomed.length === 0) return;
    for (let k = doomed.length - 1; k >= 0; k--) {
      const i = doomed[k]!;
      removePlacement(s.map, s.placements[i]!);
      s.objects.splice(i, 1);
      s.placements.splice(i, 1);
    }
    renumber(s);
  }
}
